import { format } from 'date-fns';
import { ReactNode } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import CustomerStatsStrip from './CustomerStatsStrip';
import CustomerContactInfo from './CustomerContactInfo';
import CustomerGuestProfilesSection from './CustomerGuestProfilesSection';
import CustomerBalancesSection from './CustomerBalancesSection';
import { CustomerOverview, CustomerStats } from '../../../types/customers';
import { SURFACE_HOVER } from './colors';
import Badge from '../../global/Badge';
import { getCustomerBlockBadges } from '../../../helpers/customers/customerBlockBadge';

interface Props {
  customer: CustomerOverview;
  stats: CustomerStats | undefined;
  isStatsLoading: boolean;
  businessId: string;
  actions?: ReactNode;
  onEditCustomer: () => void;
  onGuestChanged: () => unknown;
}

function CustomerSummaryCard({
  customer,
  stats,
  isStatsLoading,
  businessId,
  actions,
  onEditCustomer,
  onGuestChanged,
}: Props) {
  const customerName = `${customer.user.firstName || ''} ${
    customer.user.lastName || ''
  }`.trim();
  const initials = [customer.user.firstName?.[0], customer.user.lastName?.[0]]
    .filter(Boolean)
    .join('')
    .toUpperCase();
  const clientSince =
    !isStatsLoading && stats
      ? format(new Date(stats.customerSince), 'MMMM yyyy')
      : null;
  const statusBadges = getCustomerBlockBadges({
    customer,
    isActive: stats?.isActive ?? true,
  });

  return (
    <Stack>
      {actions}

      <Stack alignItems="center" textAlign="center">
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: 92,
            height: 92,
            borderRadius: '50%',
            backgroundColor: 'common.black',
            color: 'common.white',
            typography: 'display',
          }}>
          {initials || '?'}
        </Stack>
        <Typography
          variant="h1"
          sx={{
            mt: '20px',
            color: 'text.primary',
            lineHeight: 1.2,
          }}>
          {customerName || '-'}
        </Typography>
        {clientSince && (
          <Typography
            variant="body2"
            sx={{ color: 'text.tertiary', mt: '8px' }}>
            Client since {clientSince}
          </Typography>
        )}
        <Stack
          direction="row"
          justifyContent="center"
          flexWrap="wrap"
          gap="6px"
          sx={{ mt: '12px' }}>
          {statusBadges.map(badge => (
            <Badge
              key={badge.text}
              text={badge.text}
              colorScheme={badge.colorScheme}
              rounded
            />
          ))}
        </Stack>

        <Button
          onClick={onEditCustomer}
          sx={{
            mt: '20px',
            height: 40,
            px: '26px',
            borderRadius: '50px',
            border: '1px solid',
            borderColor: 'common.black',
            backgroundColor: 'common.white',
            color: 'text.primary',
            textTransform: 'none',
            '&:hover': { backgroundColor: SURFACE_HOVER },
            typography: 'h5',
            fontWeight: 700,
          }}>
          Edit customer
        </Button>
      </Stack>

      <CustomerStatsStrip stats={stats} isLoading={isStatsLoading} />
      <CustomerContactInfo customer={customer} />
      <CustomerBalancesSection customer={customer} />
      <CustomerGuestProfilesSection
        customerId={customer.customerId}
        businessId={businessId}
        guests={customer.customerGuests ?? []}
        onGuestChanged={onGuestChanged}
      />
    </Stack>
  );
}

export default CustomerSummaryCard;
